const STORAGE_PREFIX = 'quadratik:search';
const SCHEMA_VERSION = 1;
const IDB_DB_NAME = 'quadratik';
const IDB_DB_VERSION = 1;
const IDB_RESULTS_STORE = 'searchResults';

function makeKey(kind, userId) {
  const safeUserId = userId ? String(userId) : 'anon';
  return `${STORAGE_PREFIX}:${kind}:v${SCHEMA_VERSION}:${safeUserId}`;
}

function safeJsonParse(str) {
  if (typeof str !== 'string' || !str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function safeGetItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemoveItem(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function nowMs() {
  return Date.now();
}

function isExpired(savedAtMs, ttlMs) {
  if (!savedAtMs || typeof savedAtMs !== 'number') return true;
  return nowMs() - savedAtMs > ttlMs;
}

export function loadSearchFilterDraft(userId) {
  const key = makeKey('filtersDraft', userId);
  const raw = safeGetItem(key);
  const parsed = safeJsonParse(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  // No TTL on filters; they should just persist until changed/cleared.
  return parsed.data ?? null;
}

export function saveSearchFilterDraft(userId, draft) {
  const key = makeKey('filtersDraft', userId);
  const payload = {
    schemaVersion: SCHEMA_VERSION,
    savedAtMs: nowMs(),
    data: draft ?? null,
  };
  safeSetItem(key, JSON.stringify(payload));
}

const RESULTS_TTL_MS = 1000 * 60 * 60 * 24 * 3; // 3 days
const RESULTS_VIEW_TTL_MS = RESULTS_TTL_MS;

export function loadSearchResultsViewState(userId) {
  const key = makeKey('resultsView', userId);
  const raw = safeGetItem(key);
  const parsed = safeJsonParse(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  if (isExpired(parsed.savedAtMs, RESULTS_VIEW_TTL_MS)) {
    safeRemoveItem(key);
    return null;
  }
  return parsed.data ?? null;
}

export function saveSearchResultsViewState(userId, viewState) {
  const key = makeKey('resultsView', userId);
  const payload = {
    schemaVersion: SCHEMA_VERSION,
    savedAtMs: nowMs(),
    data: viewState ?? null,
  };
  safeSetItem(key, JSON.stringify(payload));
}

export function loadSearchResultsSelectionState(userId) {
  const key = makeKey('resultsSelection', userId);
  const raw = safeGetItem(key);
  const parsed = safeJsonParse(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  if (isExpired(parsed.savedAtMs, RESULTS_TTL_MS)) {
    safeRemoveItem(key);
    return null;
  }
  return parsed.data ?? null;
}

export function saveSearchResultsSelectionState(userId, selectionState) {
  const key = makeKey('resultsSelection', userId);
  const payload = {
    schemaVersion: SCHEMA_VERSION,
    savedAtMs: nowMs(),
    data: selectionState ?? null,
  };
  safeSetItem(key, JSON.stringify(payload));
}

function openIdb() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('indexedDB not available'));
      return;
    }

    const req = window.indexedDB.open(IDB_DB_NAME, IDB_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_RESULTS_STORE)) {
        db.createObjectStore(IDB_RESULTS_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('failed to open indexedDB'));
  });
}

async function idbGet(storeName, key) {
  const db = await openIdb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error || new Error('idb get failed'));
    });
  } finally {
    db.close();
  }
}

async function idbSet(storeName, key, value) {
  const db = await openIdb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error || new Error('idb put failed'));
    });
  } finally {
    db.close();
  }
}

async function idbDelete(storeName, key) {
  const db = await openIdb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error || new Error('idb delete failed'));
    });
  } finally {
    db.close();
  }
}

// Results are stored in IndexedDB to avoid localStorage quota/truncation.
// We keep a small localStorage metadata record for quick presence/TTL checks and backward compatibility.
export async function loadSearchResultsCache(userId) {
  const key = makeKey('results', userId);

  // Backward-compat: if old localStorage payload exists with embedded data, use it.
  const raw = safeGetItem(key);
  const parsed = safeJsonParse(raw);
  if (parsed && typeof parsed === 'object' && parsed.data) {
    if (isExpired(parsed.savedAtMs, RESULTS_TTL_MS)) {
      safeRemoveItem(key);
      return null;
    }
    return parsed.data ?? null;
  }

  try {
    const payload = await idbGet(IDB_RESULTS_STORE, key);
    if (!payload || typeof payload !== 'object') return null;
    if (isExpired(payload.savedAtMs, RESULTS_TTL_MS)) {
      await idbDelete(IDB_RESULTS_STORE, key);
      safeRemoveItem(key);
      return null;
    }
    return payload.data ?? null;
  } catch {
    return null;
  }
}

export async function saveSearchResultsCache(userId, results) {
  const key = makeKey('results', userId);
  const payload = {
    schemaVersion: SCHEMA_VERSION,
    savedAtMs: nowMs(),
    data: results ?? null,
  };

  // Store full payload in IndexedDB (preferred).
  try {
    await idbSet(IDB_RESULTS_STORE, key, payload);
  } catch {
    // ignore and fall back to metadata-only localStorage
  }

  // Metadata for presence/TTL + optional debugging.
  const meta = {
    schemaVersion: SCHEMA_VERSION,
    savedAtMs: payload.savedAtMs,
    inIdb: true,
    contactsLength: Array.isArray(results?.contacts) ? results.contacts.length : 0,
    totalCount: typeof results?.totalCount === 'number' ? results.totalCount : null,
  };
  safeSetItem(key, JSON.stringify(meta));
}

export function migrateSearchPersistence(fromUserId, toUserId) {
  const from = fromUserId ? String(fromUserId) : 'anon';
  const to = toUserId ? String(toUserId) : 'anon';
  if (!from || !to || from === to) return;

  // Migrate filters draft if destination missing.
  const fromFiltersKey = makeKey('filtersDraft', from);
  const toFiltersKey = makeKey('filtersDraft', to);
  if (!safeGetItem(toFiltersKey)) {
    const raw = safeGetItem(fromFiltersKey);
    if (raw) safeSetItem(toFiltersKey, raw);
  }

  // Migrate results if destination missing.
  const fromResultsKey = makeKey('results', from);
  const toResultsKey = makeKey('results', to);
  if (!safeGetItem(toResultsKey)) {
    const raw = safeGetItem(fromResultsKey);
    if (raw) safeSetItem(toResultsKey, raw);
  }

  // Migrate results view state (pagination, etc.) if destination missing.
  const fromResultsViewKey = makeKey('resultsView', from);
  const toResultsViewKey = makeKey('resultsView', to);
  if (!safeGetItem(toResultsViewKey)) {
    const raw = safeGetItem(fromResultsViewKey);
    if (raw) safeSetItem(toResultsViewKey, raw);
  }

  // Migrate results selection state if destination missing.
  const fromSelectionKey = makeKey('resultsSelection', from);
  const toSelectionKey = makeKey('resultsSelection', to);
  if (!safeGetItem(toSelectionKey)) {
    const raw = safeGetItem(fromSelectionKey);
    if (raw) safeSetItem(toSelectionKey, raw);
  }

  // Best-effort migrate IndexedDB results.
  // (Async, but fire-and-forget; callers don't need to await.)
  (async () => {
    try {
      const existing = await idbGet(IDB_RESULTS_STORE, toResultsKey);
      if (existing) return;
      const fromPayload = await idbGet(IDB_RESULTS_STORE, fromResultsKey);
      if (fromPayload) {
        await idbSet(IDB_RESULTS_STORE, toResultsKey, fromPayload);
      }
    } catch {
      // ignore
    }
  })();
}

